import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Search, Calendar, User, MapPin, Wind, ClipboardList, Clock, Activity, X, List, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { useSubscription, SUBSCRIPTION_TYPES } from '../../hooks/useSubscription';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const ProjectManagement = () => {
  const { hasSubscription } = useSubscription();
  const hasCPOSubscription = hasSubscription(SUBSCRIPTION_TYPES.CONSTRUCTION_PROJECT_ORDER);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [recentlyToggledProjects, setRecentlyToggledProjects] = useState(new Set()); // Track projects being toggled
  
  // Work Planning States
  const [activeTab, setActiveTab] = useState('projects'); // 'projects', 'planning', or 'site-status'
  const [selectedPlanningProject, setSelectedPlanningProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workPlans, setWorkPlans] = useState([]);
  
  // New Planning System States
  const [planningMode, setPlanningMode] = useState('turbine'); // 'turbine' or 'daily'
  const [selectedTurbine, setSelectedTurbine] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [turbinePlan, setTurbinePlan] = useState([]); // [{id, taskId, startTime, endTime, assignedUsers: [userId]}]
  const [showTaskAssignmentModal, setShowTaskAssignmentModal] = useState(false);
  const [taskAssignmentData, setTaskAssignmentData] = useState({
    taskId: '',
    startTime: '08:00',
    endTime: '16:00',
    assignedUsers: []
  });
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Daily overview data
  const [dailyPlans, setDailyPlans] = useState({}); // {date: {turbineId: [assignments]}}
  
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    name: '',
    description: '',
    turbine_number: '',
    estimated_hours: '',
    priority: 'medium'
  });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planFormData, setPlanFormData] = useState({
    project_id: '',
    task_id: '',
    user_id: '',
    turbine_number: '',
    date: '',
    start_time: '08:00',
    end_time: '16:00',
    notes: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_number: '',
    project_manager: '',
    customer: '',
    country: '',
    street_address: '',
    city: '',
    postal_code: '',
    latitude: '',
    longitude: '',
    start_date: '',
    end_date: '',
    type_of_turbine: '',
    max_days_per_employers: '',
    color: '#3B82F6',
    comment: '',
    required_documents: [],
    region: '',
    model: '',
    power_rating: '',
    type_swo_admin: '',
    type_swo_instalation: '',
    project_type: '',
    need_check: false
  });
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [projectTurbines, setProjectTurbines] = useState([]); // Turbines from API
  const [regions, setRegions] = useState([]);
  const [savingTurbines, setSavingTurbines] = useState(false);

  // Task Category Modal States (legacy - for project-based categories)
  const [showTaskCategoryModal, setShowTaskCategoryModal] = useState(false);
  const [projectForTasks, setProjectForTasks] = useState(null);
  const [taskCategoryData, setTaskCategoryData] = useState({
    categoryName: '',
    categoryColor: '#3B82F6',
    subcategories: [{ name: '', description: '', estimated_hours: '' }]
  });
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [projectTaskCategories, setProjectTaskCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [projectTaskCounts, setProjectTaskCounts] = useState({}); // {projectId: count}

  // Task Lists States (new system - reusable task lists)
  const [taskLists, setTaskLists] = useState([]);
  const [taskListsLoading, setTaskListsLoading] = useState(false);
  const [showTaskListModal, setShowTaskListModal] = useState(false);
  const [editingTaskList, setEditingTaskList] = useState(null);
  const [taskListFormData, setTaskListFormData] = useState({
    name: '',
    description: '',
    is_default: true,
    project: null,
    categories: [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
  });
  const [expandedTaskListCategories, setExpandedTaskListCategories] = useState(new Set());
  const [taskListSearchTerm, setTaskListSearchTerm] = useState('');
  const [showNonDefaultTaskLists, setShowNonDefaultTaskLists] = useState(false); // When checked: show all lists including non-default
  // State for category form within Task List modal
  const [editingTaskListCategory, setEditingTaskListCategory] = useState(null); // index of category being edited
  const [taskListCategoryFormData, setTaskListCategoryFormData] = useState({
    categoryName: '',
    categoryColor: '#3B82F6',
    subcategories: [{ name: '', description: '', estimated_hours: '' }]
  });
  // State for creating/editing Task List from Project form - stores turbine index or null
  const [creatingTaskListForTurbine, setCreatingTaskListForTurbine] = useState(null);
  const [editingTaskListForTurbine, setEditingTaskListForTurbine] = useState(null);
  const [originalTaskListName, setOriginalTaskListName] = useState(null);
  // State for previewing Task List in Project modal
  const [previewingTaskList, setPreviewingTaskList] = useState(null);
  const [expandedPreviewCategories, setExpandedPreviewCategories] = useState(new Set());
  // State for searchable Task List dropdown per turbine
  const [turbineTaskListSearch, setTurbineTaskListSearch] = useState({});
  const [turbineTaskListDropdownOpen, setTurbineTaskListDropdownOpen] = useState(null);

  // Close task list dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (turbineTaskListDropdownOpen !== null && !e.target.closest('.task-list-dropdown')) {
        setTurbineTaskListDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [turbineTaskListDropdownOpen]);

  useEffect(() => {
    fetchProjects();
    fetchDocuments();
    fetchUsers();
    fetchTasks();
    fetchWorkPlans();
    fetchRegions();
    fetchAllTaskCategoryCounts();
    fetchTaskLists();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/projects/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Temporarily disable required documents until backend endpoint is implemented
      setAvailableDocuments([]);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/task/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchWorkPlans = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Temporarily disable work plans until backend endpoint is implemented
      setWorkPlans([]);
    } catch (error) {
      console.error('Error fetching work plans:', error);
    }
  };

  // Fetch turbines for a specific project
  const fetchProjectTurbines = async (projectId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/project-turbines/?project=${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching project turbines:', error);
      return [];
    }
  };

  // Create a copy of a TaskList (for converting default templates to project-specific lists)
  // Returns: { success: true, taskList: {...} } or { success: false, error: '...' }
  const createTaskListCopy = async (taskListId, projectId, projectName) => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      // Fetch the full TaskList with categories and subcategories from API
      const originalResponse = await axios.get(`${API_BASE_URL}/api/task-lists/${taskListId}/`, { headers });
      const originalTaskList = originalResponse.data;

      if (!originalTaskList) {
        console.error('TaskList not found:', taskListId);
        return { success: false, error: `Task list not found: ${taskListId}` };
      }

      const expectedCopyName = `${originalTaskList.name} - ${projectName}`;

      // Check if a task list with this name already exists
      const existingTaskList = taskLists.find(tl => tl.name === expectedCopyName);

      if (existingTaskList) {
        // Task list with this name exists - check if it belongs to our project
        if (existingTaskList.project === projectId) {
          console.log('Found existing TaskList copy for this project:', existingTaskList.name);
          return { success: true, taskList: existingTaskList };
        } else {
          // Exists but belongs to different project - this shouldn't happen normally
          console.error('TaskList with name exists but belongs to different project:', existingTaskList);
          return {
            success: false,
            error: `Task list "${expectedCopyName}" already exists but belongs to a different project.`
          };
        }
      }

      console.log('Copying TaskList:', originalTaskList.name, 'with', originalTaskList.categories?.length, 'categories');

      // Prepare categories with subcategories for the copy
      const categoriesData = (originalTaskList.categories || []).map(cat => {
        console.log('Category:', cat.name, 'has', cat.subcategories?.length, 'subcategories');
        return {
          name: cat.name,
          color: cat.color,
          order: cat.order,
          subcategories: (cat.subcategories || []).map(sub => ({
            name: sub.name,
            description: sub.description || '',
            estimated_hours: sub.estimated_hours,
            order: sub.order
          }))
        };
      });

      // Create new TaskList with modified name, is_default = false, and assigned to project
      const newTaskListData = {
        name: expectedCopyName,
        description: originalTaskList.description || '',
        is_default: false,
        project: projectId,
        categories: categoriesData
      };

      console.log('Creating TaskList copy with data:', JSON.stringify(newTaskListData, null, 2));

      const response = await axios.post(`${API_BASE_URL}/api/task-lists/`, newTaskListData, { headers });

      // Fetch the newly created TaskList with full nested data
      const newTaskListResponse = await axios.get(`${API_BASE_URL}/api/task-lists/${response.data.id}/`, { headers });

      // Add the new TaskList to state so it's available immediately
      setTaskLists(prev => [...prev, newTaskListResponse.data]);

      return { success: true, taskList: newTaskListResponse.data };
    } catch (error) {
      console.error('Error creating TaskList copy:', error);
      console.error('Error response:', error.response?.data);

      // Check if error is due to duplicate name (UNIQUE constraint)
      const errorMsg = error.response?.data?.name?.[0] || error.response?.data?.detail || error.message;
      if (errorMsg?.includes('UNIQUE') || errorMsg?.includes('unique') || errorMsg?.includes('already exists')) {
        // Fetch all task lists directly from API to find the existing one
        try {
          const allTaskListsResponse = await axios.get(`${API_BASE_URL}/api/task-lists/`, { headers });
          const allTaskLists = allTaskListsResponse.data;
          const originalName = (await axios.get(`${API_BASE_URL}/api/task-lists/${taskListId}/`, { headers })).data.name;
          const expectedCopyName = `${originalName} - ${projectName}`;
          const existingTaskList = allTaskLists.find(tl => tl.name === expectedCopyName);

          if (existingTaskList && existingTaskList.project === projectId) {
            // Update local state with fresh data
            setTaskLists(allTaskLists);
            return { success: true, taskList: existingTaskList };
          }

          return {
            success: false,
            error: `Task list "${expectedCopyName}" already exists but belongs to a different project.`
          };
        } catch (fetchError) {
          console.error('Error fetching task lists after duplicate error:', fetchError);
        }
      }

      return { success: false, error: errorMsg || 'Unknown error creating task list copy' };
    }
  };

  // Save turbines for a project (create new, update existing, delete removed)
  const saveProjectTurbines = async (projectId, turbines, projectName) => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      setSavingTurbines(true);

      // Get existing turbines
      const existingTurbines = await fetchProjectTurbines(projectId);
      const existingIds = existingTurbines.map(t => t.id);
      const newTurbineIds = turbines.filter(t => t.id).map(t => t.id);

      // Delete turbines that are no longer in the list
      const toDelete = existingIds.filter(id => !newTurbineIds.includes(id));
      for (const id of toDelete) {
        await axios.delete(`${API_BASE_URL}/api/project-turbines/${id}/`, { headers });
      }

      // Convert default TaskLists to project-specific copies
      // Step 1: Find all unique default TaskLists used by turbines
      const defaultTaskListIds = new Set();
      for (const turbine of turbines) {
        if (turbine.task_list) {
          const taskList = taskLists.find(tl => tl.id === turbine.task_list);
          if (taskList && taskList.is_default) {
            defaultTaskListIds.add(turbine.task_list);
          }
        }
      }

      // Step 2: Create a copy for each unique default TaskList (only once per default)
      const taskListCopyMap = {}; // Maps original default TaskList ID -> new copy ID
      const errors = [];

      for (const defaultTaskListId of defaultTaskListIds) {
        const result = await createTaskListCopy(defaultTaskListId, projectId, projectName);
        if (result.success) {
          taskListCopyMap[defaultTaskListId] = result.taskList.id;
        } else {
          errors.push(result.error);
        }
      }

      // If there were errors creating task list copies, show them and abort
      if (errors.length > 0) {
        alert(`Error creating task list copies:\n${errors.join('\n')}`);
        setSavingTurbines(false);
        return;
      }

      // Step 3: Update turbines - replace default TaskList IDs with copy IDs
      const updatedTurbines = turbines.map(turbine => {
        if (turbine.task_list && taskListCopyMap[turbine.task_list]) {
          return { ...turbine, task_list: taskListCopyMap[turbine.task_list] };
        }
        return turbine;
      });

      // Update or create turbines
      for (const turbine of updatedTurbines) {
        const turbineData = {
          project: projectId,
          turbine_number: turbine.turbine_number,
          turbine_id: turbine.turbine_id || null,
          model: turbine.model || null,
          power_rating: turbine.power_rating || null,
          latitude: turbine.latitude || null,
          longitude: turbine.longitude || null,
          task_list: turbine.task_list || null
        };

        if (turbine.id) {
          // Update existing
          await axios.put(`${API_BASE_URL}/api/project-turbines/${turbine.id}/`, turbineData, { headers });
        } else {
          // Create new
          await axios.post(`${API_BASE_URL}/api/project-turbines/`, turbineData, { headers });
        }
      }

      return true;
    } catch (error) {
      console.error('Error saving project turbines:', error);
      return false;
    } finally {
      setSavingTurbines(false);
    }
  };

  // Initialize empty turbines array based on count
  const initializeTurbines = (count, defaultModel = '', defaultPowerRating = '') => {
    return Array.from({ length: count }, (_, index) => ({
      turbine_number: index + 1,
      turbine_id: '',
      model: defaultModel,
      power_rating: defaultPowerRating,
      latitude: '',
      longitude: ''
    }));
  };

  // Save or update administration marker for project
  const saveAdministrationMarker = async (projectId, latitude, longitude) => {
    if (!latitude || !longitude) return;

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      // Check if administration marker already exists for this project
      const response = await axios.get(
        `${API_BASE_URL}/api/project-markers/?project=${projectId}&marker_type=administration`,
        { headers }
      );

      const markerData = {
        project: projectId,
        marker_type: 'administration',
        name: 'Administration',
        latitude: latitude,
        longitude: longitude
      };

      if (response.data && response.data.length > 0) {
        // Update existing marker
        await axios.put(`${API_BASE_URL}/api/project-markers/${response.data[0].id}/`, markerData, { headers });
      } else {
        // Create new marker
        await axios.post(`${API_BASE_URL}/api/project-markers/`, markerData, { headers });
      }
    } catch (error) {
      console.error('Error saving administration marker:', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.project_type) {
      alert('Please select a project type (Service or Installation)');
      return;
    }

    // Clean up form data - remove empty dates
    const cleanedFormData = { ...formData };
    if (!cleanedFormData.start_date || cleanedFormData.start_date === '') {
      delete cleanedFormData.start_date;
    }
    if (!cleanedFormData.end_date || cleanedFormData.end_date === '') {
      delete cleanedFormData.end_date;
    }

    // Remove turbine_coordinates from project data (now handled by ProjectTurbine model)
    delete cleanedFormData.turbine_coordinates;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_BASE_URL}/api/projects/`, cleanedFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const newProject = response.data;

      // Save turbines for the new project
      if (projectTurbines.length > 0) {
        await saveProjectTurbines(newProject.id, projectTurbines, newProject.name);
      }

      // Create administration marker if coordinates provided
      if (formData.latitude && formData.longitude) {
        await saveAdministrationMarker(newProject.id, formData.latitude, formData.longitude);
      }

      setProjects([...projects, newProject]);
      setShowCreateModal(false);
      setPreviewingTaskList(null);
      resetForm();
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Sent data:', cleanedFormData);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.project_type) {
      alert('Please select a project type (Service or Installation)');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');

      // Clean up form data - remove empty dates and ensure proper format
      const cleanedFormData = { ...formData };
      if (!cleanedFormData.start_date || cleanedFormData.start_date === '') {
        delete cleanedFormData.start_date;
      }
      if (!cleanedFormData.end_date || cleanedFormData.end_date === '') {
        delete cleanedFormData.end_date;
      }

      // Ensure required_documents is an array of IDs, not objects
      if (cleanedFormData.required_documents && Array.isArray(cleanedFormData.required_documents)) {
        cleanedFormData.required_documents = cleanedFormData.required_documents.map(doc =>
          typeof doc === 'object' ? doc.id : doc
        ).filter(Boolean);
      }

      // Remove turbine_coordinates from project data (now handled by ProjectTurbine model)
      delete cleanedFormData.turbine_coordinates;

      // Log the data being sent for debugging
      console.log('Updating project with data:', cleanedFormData);

      const response = await axios.put(`${API_BASE_URL}/api/projects/${selectedProject.id}/`, cleanedFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Save turbines for the project
      await saveProjectTurbines(selectedProject.id, projectTurbines, response.data.name);

      // Update administration marker if coordinates provided
      if (formData.latitude && formData.longitude) {
        await saveAdministrationMarker(selectedProject.id, formData.latitude, formData.longitude);
      }

      setProjects(projects.map(p => p.id === selectedProject.id ? response.data : p));
      setShowEditModal(false);
      setSelectedProject(null);
      setPreviewingTaskList(null);
      resetForm();
    } catch (error) {
      console.error('Error updating project:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
    }
  };

  const handleDelete = async (project) => {
    try {
      // Check if project has issued items in warehouse
      const token = localStorage.getItem('access_token');
      const warehouseResponse = await axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Filter issues for this project that have outstanding items
      const projectIssues = warehouseResponse.data.filter(issue =>
        issue.project === project.id &&
        (issue.status === 'issued' || issue.status === 'partially_returned')
      );

      if (projectIssues.length > 0) {
        // Calculate total outstanding items
        let totalOutstanding = 0;
        projectIssues.forEach(issue => {
          if (issue.items && Array.isArray(issue.items)) {
            issue.items.forEach(item => {
              const outstanding = (item.quantity_issued || 0) - (item.quantity_returned || 0);
              totalOutstanding += outstanding;
            });
          }
        });

        alert(
          `Cannot delete project "${project.name}"!\n\n` +
          `This project has ${totalOutstanding} outstanding item(s) in the warehouse.\n\n` +
          `Please return all issued items first:\n` +
          `- Go to Warehouse → History\n` +
          `- Find this project and return all items\n` +
          `- Or use manual return in Warehouse → Inventory for orphaned items`
        );
        return;
      }

      // If no issues, proceed with deletion
      if (window.confirm(`Are you sure you want to delete project "${project.name}"?`)) {
        await axios.delete(`${API_BASE_URL}/api/projects/${project.id}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setProjects(projects.filter(p => p.id !== project.id));
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      if (error.response?.status === 404) {
        // Warehouse API not available, allow deletion with warning
        if (window.confirm(
          `Warning: Could not verify warehouse items for project "${project.name}".\n\n` +
          `Are you sure you want to delete this project?`
        )) {
          try {
            const token = localStorage.getItem('access_token');
            await axios.delete(`${API_BASE_URL}/api/projects/${project.id}/`, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            setProjects(projects.filter(p => p.id !== project.id));
          } catch (deleteError) {
            console.error('Error deleting project:', deleteError);
            alert('Failed to delete project. Please try again.');
          }
        }
      } else {
        alert('Failed to check warehouse items. Please try again.');
      }
    }
  };

  const handleToggleActive = async (project) => {
    // Treat undefined/null as true (active by default)
    const currentStatus = project.is_active !== false;
    const newActiveStatus = !currentStatus;

    // OPTIMISTIC UPDATE - update UI immediately
    setProjects(projects.map(p =>
      p.id === project.id ? { ...p, is_active: newActiveStatus } : p
    ));

    // If toggling from active to inactive, add to recently toggled and remove after delay
    if (currentStatus === true && newActiveStatus === false) {
      setRecentlyToggledProjects(prev => new Set(prev).add(project.id));

      // Remove from recently toggled after 2.5 seconds
      setTimeout(() => {
        setRecentlyToggledProjects(prev => {
          const newSet = new Set(prev);
          newSet.delete(project.id);
          return newSet;
        });
      }, 2500);
    }

    try {
      const token = localStorage.getItem('access_token');

      // Get current project data
      const currentResponse = await axios.get(`${API_BASE_URL}/api/projects/${project.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Update with all current data plus new is_active
      const updateData = {
        ...currentResponse.data,
        is_active: newActiveStatus
      };

      await axios.put(
        `${API_BASE_URL}/api/projects/${project.id}/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log(`✓ Project "${project.name}" ${newActiveStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling project active status:', error);

      // ROLLBACK - revert optimistic update on error
      setProjects(projects.map(p =>
        p.id === project.id ? { ...p, is_active: currentStatus } : p
      ));

      // Remove from recently toggled on error
      setRecentlyToggledProjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(project.id);
        return newSet;
      });

      alert('Failed to update project status. Please try again.');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const taskData = {
        ...taskFormData,
        project_id: selectedPlanningProject.id
      };
      const response = await axios.post(`${API_BASE_URL}/api/task/`, taskData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setTasks([...tasks, response.data]);
      setShowTaskModal(false);
      setTaskFormData({
        name: '',
        description: '',
        turbine_number: '',
        estimated_hours: '',
        priority: 'medium'
      });
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      project_number: '',
      project_manager: '',
      customer: '',
      country: '',
      street_address: '',
      city: '',
      postal_code: '',
      latitude: '',
      longitude: '',
      start_date: '',
      end_date: '',
      type_of_turbine: '',
      max_days_per_employers: '',
      color: '#3B82F6',
      comment: '',
      required_documents: [],
      region: '',
      model: '',
      power_rating: '',
      type_swo_admin: '',
      type_swo_instalation: '',
      project_type: '',
      need_check: false
    });
    setProjectTurbines([]);
  };

  const fetchRegions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/regions/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchAllTaskCategoryCounts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Fetch all project turbines with their task lists (include_tasks=true returns full task list data)
      const turbinesResponse = await axios.get(`${API_BASE_URL}/api/project-turbines/?include_tasks=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Count subcategories per project (from task lists assigned to turbines)
      const counts = {};
      turbinesResponse.data.forEach(turbine => {
        const projectId = turbine.project;
        if (turbine.task_list_data && turbine.task_list_data.categories) {
          turbine.task_list_data.categories.forEach(category => {
            const subcategoryCount = category.subcategories ? category.subcategories.length : 0;
            counts[projectId] = (counts[projectId] || 0) + subcategoryCount;
          });
        }
      });
      setProjectTaskCounts(counts);
    } catch (error) {
      console.error('Error fetching task category counts:', error);
    }
  };

  // ==================== Task Lists Functions (new system) ====================

  const fetchTaskLists = async () => {
    try {
      setTaskListsLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/task-lists/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskLists(response.data);
    } catch (error) {
      console.error('Error fetching task lists:', error);
    } finally {
      setTaskListsLoading(false);
    }
  };

  const openCreateTaskListModal = () => {
    setEditingTaskList(null);
    setTaskListFormData({
      name: '',
      description: '',
      is_default: true,
      project: null,
      categories: [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
    });
    setEditingTaskListCategory(null);
    setTaskListCategoryFormData({
      categoryName: '',
      categoryColor: '#3B82F6',
      subcategories: [{ name: '', description: '', estimated_hours: '' }]
    });
    setExpandedTaskListCategories(new Set());
    setShowTaskListModal(true);
  };

  const openEditTaskListModal = (taskList) => {
    setEditingTaskList(taskList);
    setTaskListFormData({
      name: taskList.name,
      description: taskList.description || '',
      is_default: taskList.is_default !== undefined ? taskList.is_default : true,
      project: taskList.project || null,
      categories: taskList.categories?.length > 0
        ? taskList.categories.map(cat => ({
            name: cat.name,
            color: cat.color || '#3B82F6',
            subcategories: cat.subcategories?.length > 0
              ? cat.subcategories.map(sub => ({
                  name: sub.name,
                  description: sub.description || '',
                  estimated_hours: sub.estimated_hours || ''
                }))
              : [{ name: '', description: '', estimated_hours: '' }]
          }))
        : [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
    });
    setEditingTaskListCategory(null);
    setTaskListCategoryFormData({
      categoryName: '',
      categoryColor: '#3B82F6',
      subcategories: [{ name: '', description: '', estimated_hours: '' }]
    });
    setExpandedTaskListCategories(new Set());
    setShowTaskListModal(true);
  };

  const handleSaveTaskList = async () => {
    if (!taskListFormData.name.trim()) {
      alert('Please enter a task list name');
      return;
    }

    // Validate project is selected for non-default task lists
    if (!taskListFormData.is_default && !taskListFormData.project) {
      alert('Please select a project for non-default task list');
      return;
    }

    // When editing from project form, validate name is different from original
    if (editingTaskListForTurbine !== null && originalTaskListName) {
      if (taskListFormData.name.trim() === originalTaskListName) {
        alert('Please change the name to create a copy of the Task List');
        return;
      }
    }

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    // Filter out empty categories and subcategories
    const cleanedCategories = taskListFormData.categories
      .filter(cat => cat.name.trim())
      .map(cat => ({
        name: cat.name.trim(),
        color: cat.color || '#3B82F6',
        subcategories: cat.subcategories
          .filter(sub => sub.name.trim())
          .map(sub => ({
            name: sub.name.trim(),
            description: sub.description || '',
            estimated_hours: sub.estimated_hours || null
          }))
      }));

    const payload = {
      name: taskListFormData.name.trim(),
      description: taskListFormData.description.trim(),
      is_default: taskListFormData.is_default,
      project: taskListFormData.is_default ? null : taskListFormData.project,
      categories: cleanedCategories
    };

    try {
      let newTaskListId = null;

      // When editing from project form, always create new (copy)
      if (editingTaskListForTurbine !== null) {
        const response = await axios.post(`${API_BASE_URL}/api/task-lists/`, payload, { headers });
        newTaskListId = response.data.id;
      } else if (editingTaskList) {
        // Update existing (from Task Lists tab)
        await axios.put(`${API_BASE_URL}/api/task-lists/${editingTaskList.id}/`, payload, { headers });
        newTaskListId = editingTaskList.id;
      } else {
        // Create new
        const response = await axios.post(`${API_BASE_URL}/api/task-lists/`, payload, { headers });
        newTaskListId = response.data.id;
      }

      setShowTaskListModal(false);
      await fetchTaskLists();

      // If creating from project form, assign the new task list to the specific turbine
      if (creatingTaskListForTurbine !== null && newTaskListId) {
        setProjectTurbines(prev => prev.map((t, idx) =>
          idx === creatingTaskListForTurbine ? { ...t, task_list: newTaskListId } : t
        ));
        setCreatingTaskListForTurbine(null);
      }

      // If editing from project form (creating a copy), assign the new task list to the specific turbine
      if (editingTaskListForTurbine !== null && newTaskListId) {
        setProjectTurbines(prev => prev.map((t, idx) =>
          idx === editingTaskListForTurbine ? { ...t, task_list: newTaskListId } : t
        ));
        setEditingTaskListForTurbine(null);
        setOriginalTaskListName(null);
      }
    } catch (error) {
      console.error('Error saving task list:', error);
      alert('Error saving task list: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTaskList = async (taskListId, taskListName) => {
    if (!window.confirm(`Are you sure you want to delete "${taskListName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/api/task-lists/${taskListId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTaskLists();
    } catch (error) {
      console.error('Error deleting task list:', error);
      alert('Error deleting task list: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDuplicateTaskList = async (taskList) => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const payload = {
      name: `${taskList.name} (Copy)`,
      description: taskList.description || '',
      is_default: taskList.is_default,
      project: taskList.project || null,
      categories: taskList.categories?.map(cat => ({
        name: cat.name,
        color: cat.color || '#3B82F6',
        subcategories: cat.subcategories?.map(sub => ({
          name: sub.name,
          description: sub.description || '',
          estimated_hours: sub.estimated_hours || null
        })) || []
      })) || []
    };

    try {
      await axios.post(`${API_BASE_URL}/api/task-lists/`, payload, { headers });
      fetchTaskLists();
    } catch (error) {
      console.error('Error duplicating task list:', error);
      alert('Error duplicating task list: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Task List Form Handlers
  const addTaskListCategory = () => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: [...prev.categories, { name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
    }));
  };

  const removeTaskListCategory = (categoryIndex) => {
    if (taskListFormData.categories.length <= 1) return;
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, idx) => idx !== categoryIndex)
    }));
  };

  const updateTaskListCategory = (categoryIndex, field, value) => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, idx) =>
        idx === categoryIndex ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const addTaskListSubcategory = (categoryIndex) => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, idx) =>
        idx === categoryIndex
          ? { ...cat, subcategories: [...cat.subcategories, { name: '', description: '', estimated_hours: '' }] }
          : cat
      )
    }));
  };

  const removeTaskListSubcategory = (categoryIndex, subIndex) => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, idx) =>
        idx === categoryIndex && cat.subcategories.length > 1
          ? { ...cat, subcategories: cat.subcategories.filter((_, sIdx) => sIdx !== subIndex) }
          : cat
      )
    }));
  };

  const updateTaskListSubcategory = (categoryIndex, subIndex, field, value) => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, catIdx) =>
        catIdx === categoryIndex
          ? {
              ...cat,
              subcategories: cat.subcategories.map((sub, sIdx) =>
                sIdx === subIndex ? { ...sub, [field]: value } : sub
              )
            }
          : cat
      )
    }));
  };

  const toggleTaskListCategoryExpansion = (categoryIndex) => {
    setExpandedTaskListCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryIndex)) {
        newSet.delete(categoryIndex);
      } else {
        newSet.add(categoryIndex);
      }
      return newSet;
    });
  };

  // Filter task lists by search term
  const filteredTaskLists = taskLists.filter(tl => {
    // Show only default lists unless "show non-default" is checked
    if (!showNonDefaultTaskLists && !tl.is_default) return false;
    // Filter by search term
    return tl.name.toLowerCase().includes(taskListSearchTerm.toLowerCase()) ||
      tl.description?.toLowerCase().includes(taskListSearchTerm.toLowerCase());
  }
  );

  // ==================== Task Category Functions (legacy) ====================
  const openTaskCategoryModal = async (project) => {
    setProjectForTasks(project);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/task-categories/?project=${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectTaskCategories(response.data);
    } catch (error) {
      console.error('Error loading task categories:', error);
      setProjectTaskCategories([]);
    }
    setShowTaskCategoryModal(true);
  };

  const handleSaveTaskCategory = async () => {
    if (!taskCategoryData.categoryName.trim() || !projectForTasks) return;

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (selectedCategoryForEdit) {
        const updatedCategory = {
          ...selectedCategoryForEdit,
          name: taskCategoryData.categoryName,
          color: taskCategoryData.categoryColor,
          subcategories: taskCategoryData.subcategories.filter(sub => sub.name.trim())
            .map(sub => ({
              id: sub.id || Date.now() + Math.random(),
              name: sub.name,
              description: sub.description?.trim() || '',
              estimated_hours: sub.estimated_hours ? parseFloat(sub.estimated_hours) : null
            }))
        };

        const response = await axios.put(`${API_BASE_URL}/api/task-categories/${selectedCategoryForEdit.id}/`, updatedCategory, { headers });
        setProjectTaskCategories(prev =>
          prev.map(cat => cat.id === selectedCategoryForEdit.id ? response.data : cat)
        );
      } else {
        const newCategory = {
          name: taskCategoryData.categoryName,
          color: taskCategoryData.categoryColor,
          project: projectForTasks.id,
          subcategories: taskCategoryData.subcategories.filter(sub => sub.name.trim())
            .map(sub => ({
              name: sub.name,
              description: sub.description?.trim() || '',
              estimated_hours: sub.estimated_hours ? parseFloat(sub.estimated_hours) : null
            }))
        };

        const response = await axios.post(`${API_BASE_URL}/api/task-categories/`, newCategory, { headers });
        setProjectTaskCategories(prev => [...prev, response.data]);
        // Update task count for this project
        setProjectTaskCounts(prev => ({
          ...prev,
          [projectForTasks.id]: (prev[projectForTasks.id] || 0) + 1
        }));
      }

      resetTaskCategoryForm();
    } catch (error) {
      console.error('Error saving task category:', error);

      // Handle 404 error - category was deleted
      if (error.response?.status === 404) {
        alert('This category no longer exists. The list will be refreshed.');
        // Refresh categories list
        try {
          const refreshResponse = await axios.get(`${API_BASE_URL}/api/task-categories/?project=${projectForTasks.id}`, {
            headers
          });
          setProjectTaskCategories(refreshResponse.data);
          fetchAllTaskCategoryCounts();
        } catch (refreshError) {
          console.error('Error refreshing categories:', refreshError);
        }
        resetTaskCategoryForm();
      } else {
        alert('Error saving category. Please try again.');
      }
    }
  };

  const resetTaskCategoryForm = () => {
    setTaskCategoryData({
      categoryName: '',
      categoryColor: '#3B82F6',
      subcategories: [{ name: '', description: '', estimated_hours: '' }]
    });
    setSelectedCategoryForEdit(null);
  };

  const handleEditCategory = (category) => {
    setSelectedCategoryForEdit(category);
    setTaskCategoryData({
      categoryName: category.name,
      categoryColor: category.color || '#3B82F6',
      subcategories: category.subcategories && category.subcategories.length > 0
        ? category.subcategories.map(sub => ({
            id: sub.id,
            name: sub.name,
            description: sub.description || '',
            estimated_hours: sub.estimated_hours || ''
          }))
        : [{ name: '', description: '', estimated_hours: '' }]
    });
  };

  const handleDeleteTaskCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"?\n\nThis will also delete all subcategories.`)) return;

    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`${API_BASE_URL}/api/task-categories/${categoryId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectTaskCategories(prev => prev.filter(cat => cat.id !== categoryId));
      // Update task count for this project
      if (projectForTasks) {
        setProjectTaskCounts(prev => ({
          ...prev,
          [projectForTasks.id]: Math.max(0, (prev[projectForTasks.id] || 0) - 1)
        }));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category. Please try again.');
    }
  };

  const handleAddSubcategory = () => {
    setTaskCategoryData(prev => ({
      ...prev,
      subcategories: [...prev.subcategories, { name: '', description: '', estimated_hours: '' }]
    }));
  };

  const handleUpdateSubcategory = (index, field, value) => {
    setTaskCategoryData(prev => ({
      ...prev,
      subcategories: prev.subcategories.map((sub, i) =>
        i === index ? { ...sub, [field]: value } : sub
      )
    }));
  };

  const handleRemoveSubcategory = (index) => {
    if (taskCategoryData.subcategories.length > 1) {
      setTaskCategoryData(prev => ({
        ...prev,
        subcategories: prev.subcategories.filter((_, i) => i !== index)
      }));
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleTurbineChange = (index, field, value) => {
    const newTurbines = [...projectTurbines];
    if (!newTurbines[index]) {
      newTurbines[index] = {
        turbine_number: index + 1,
        turbine_id: '',
        model: '',
        power_rating: '',
        latitude: '',
        longitude: ''
      };
    }
    newTurbines[index][field] = value;
    setProjectTurbines(newTurbines);
  };

  // Copy model and power_rating from first turbine to all others
  const applySpecsToAllTurbines = () => {
    if (projectTurbines.length < 2) return;

    const firstTurbine = projectTurbines[0];
    const model = firstTurbine.model || '';
    const powerRating = firstTurbine.power_rating || '';

    if (!model && !powerRating) {
      alert('Please fill in Model or Power Rating for the first turbine before applying to all.');
      return;
    }

    const updatedTurbines = projectTurbines.map((turbine, index) => ({
      ...turbine,
      model: model,
      power_rating: powerRating
    }));

    setProjectTurbines(updatedTurbines);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Handle project name change - update turbine IDs if turbines exist
    if (name === 'name' && projectTurbines.length > 0) {
      const projectName = value || 'Project';
      const updatedTurbines = projectTurbines.map((turbine, index) => ({
        ...turbine,
        turbine_id: `${projectName}-T${String(index + 1).padStart(2, '0')}`
      }));
      setProjectTurbines(updatedTurbines);
    }
  };

  const openCreateModal = () => {
    resetForm();
    // Start with no turbines - user can add them if needed
    setProjectTurbines([]);
    setShowCreateModal(true);
  };

  const openEditModal = async (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      project_number: project.project_number || '',
      project_manager: project.project_manager || '',
      customer: project.customer || '',
      country: project.country || '',
      street_address: project.street_address || '',
      city: project.city || '',
      postal_code: project.postal_code || '',
      latitude: project.latitude || '',
      longitude: project.longitude || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      type_of_turbine: project.type_of_turbine || '',
      max_days_per_employers: project.max_days_per_employers || '',
      color: project.color || '#3B82F6',
      comment: project.comment || '',
      required_documents: project.required_documents || [],
      region: project.region || '',
      model: project.model || '',
      power_rating: project.power_rating || '',
      type_swo_admin: project.type_swo_admin || '',
      type_swo_instalation: project.type_swo_instalation || '',
      project_type: project.project_type || '',
      need_check: project.need_check || false
    });

    // Load turbines from API
    const turbines = await fetchProjectTurbines(project.id);
    if (turbines.length > 0) {
      setProjectTurbines(turbines);
    } else {
      // Initialize with one empty turbine if project has no turbines yet
      setProjectTurbines([{
        turbine_number: 1,
        turbine_id: project.name ? `${project.name}-T01` : '',
        model: project.model || '',
        power_rating: project.power_rating || '',
        latitude: '',
        longitude: '',
        task_list: null
      }]);
    }

    setShowEditModal(true);
  };

  const filteredProjects = projects.filter(project => {
    // Text search filter
    const matchesSearch = project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.country?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'active') {
      // Keep recently toggled projects visible even if inactive
      if (recentlyToggledProjects.has(project.id)) {
        matchesStatus = true;
      } else {
        matchesStatus = project.is_active !== false; // Default to true if not set
      }
    } else if (statusFilter === 'inactive') {
      matchesStatus = project.is_active === false;
    }
    // If statusFilter === 'all', matchesStatus stays true

    return matchesSearch && matchesStatus;
  });

  const getProjectTasks = () => {
    if (!selectedPlanningProject) return [];
    return tasks.filter(task => task.project_id === selectedPlanningProject.id);
  };

  const getTurbineNumbers = () => {
    if (!selectedPlanningProject || !selectedPlanningProject.number_of_turbines) return [];
    const count = parseInt(selectedPlanningProject.number_of_turbines);
    return Array.from({ length: count }, (_, i) => i + 1);
  };

  // New planning system functions
  useEffect(() => {
    // Load turbine plan when turbine or date changes
    if (selectedTurbine && selectedDate && selectedPlanningProject) {
      loadTurbinePlan();
    }
  }, [selectedTurbine, selectedDate, selectedPlanningProject]);

  useEffect(() => {
    // Update daily plans when turbine plan changes
    updateDailyPlans();
  }, [turbinePlan]);

  const loadTurbinePlan = () => {
    // Load existing plan for selected turbine and date from dailyPlans
    const plan = dailyPlans[selectedDate]?.[selectedTurbine] || [];
    setTurbinePlan([...plan]);
  };

  const updateDailyPlans = () => {
    if (selectedTurbine && selectedDate) {
      const newDailyPlans = { ...dailyPlans };
      if (!newDailyPlans[selectedDate]) newDailyPlans[selectedDate] = {};
      newDailyPlans[selectedDate][selectedTurbine] = [...turbinePlan];
      setDailyPlans(newDailyPlans);
    }
  };

  const handleTaskAssignment = () => {
    if (!taskAssignmentData.taskId || taskAssignmentData.assignedUsers.length === 0) return;
    
    const newAssignment = {
      id: editingAssignment ? editingAssignment.id : Date.now().toString(),
      taskId: taskAssignmentData.taskId,
      startTime: taskAssignmentData.startTime,
      endTime: taskAssignmentData.endTime,
      assignedUsers: [...taskAssignmentData.assignedUsers]
    };

    if (editingAssignment) {
      // Update existing assignment
      const updatedPlan = turbinePlan.map(assignment => 
        assignment.id === editingAssignment.id ? newAssignment : assignment
      );
      setTurbinePlan(updatedPlan);
    } else {
      // Add new assignment
      setTurbinePlan([...turbinePlan, newAssignment]);
    }

    // Reset modal
    setShowTaskAssignmentModal(false);
    setEditingAssignment(null);
    setTaskAssignmentData({
      taskId: '',
      startTime: '08:00',
      endTime: '16:00',
      assignedUsers: []
    });
  };

  const removeTurbineAssignment = (assignmentId) => {
    setTurbinePlan(turbinePlan.filter(assignment => assignment.id !== assignmentId));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6">
        <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">Project Management</h1>
          {activeTab === 'task-lists' && (
            <button
              onClick={openCreateTaskListModal}
              className="flex items-center space-x-2 bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
            >
              <Plus className="h-5 w-5" />
              <span>Create Task List</span>
            </button>
          )}
        </div>
        <p className="text-slate-600 mt-2">Manage projects and plan work schedules</p>
        
        {/* Tab Navigation */}
        <div className="mt-4 border-b border-steel-200">
          <div className="flex justify-between items-center">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Wind className="h-4 w-4" />
                  <span>Projects</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('task-lists')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'task-lists'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <List className="h-4 w-4" />
                  <span>Task Lists</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('site-status')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'site-status'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4" />
                  <span>Site Status</span>
                </div>
              </button>
            </nav>
            {activeTab === 'task-lists' && (
              <div className="flex items-center space-x-4 mb-px">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search task lists..."
                    value={taskListSearchTerm}
                    onChange={(e) => setTaskListSearchTerm(e.target.value)}
                    className="pl-9 pr-3 py-1.5 text-sm border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent w-64"
                  />
                </div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showNonDefaultTaskLists}
                    onChange={(e) => setShowNonDefaultTaskLists(e.target.checked)}
                    className="h-4 w-4 text-slate-600 border-steel-300 rounded focus:ring-steel-400"
                  />
                  <span className="text-sm text-slate-700">Show non-default</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent w-80"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2 border border-brand-200 rounded-xl px-3 py-2 bg-white">
                <span className="text-sm text-slate-600 font-medium">Show:</span>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value="active"
                    checked={statusFilter === 'active'}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="mr-1.5 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-green-600 font-medium">Active</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value="inactive"
                    checked={statusFilter === 'inactive'}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="mr-1.5 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-500">Inactive</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value="all"
                    checked={statusFilter === 'all'}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="mr-1.5 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-700">All</span>
                </label>
              </div>
            </div>

            <button
              onClick={openCreateModal}
              className="flex items-center space-x-2 bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
            >
              <Plus className="h-5 w-5" />
              <span>New Project</span>
            </button>
          </div>

          {/* Projects List */}
          <div className="bg-white rounded-xl shadow overflow-hidden border border-brand-200">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                <span className="ml-3 text-slate-600">Loading projects...</span>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <Wind className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">
                  {projects.length === 0 ? 'No projects created yet' : 'No projects match your search'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-steel-200 border border-brand-200">
                  <thead className="bg-steel-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Project Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Project Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Manager
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Turbines
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Tasks
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-200">
                    {filteredProjects.map((project) => (
                      <tr
                        key={project.id}
                        className="hover:bg-steel-50 cursor-pointer"
                        onDoubleClick={() => openEditModal(project)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center max-w-[250px]">
                            <div
                              className="h-3 w-3 rounded-full mr-3 flex-shrink-0"
                              style={{ backgroundColor: project.color || '#3B82F6' }}
                            />
                            <div className="text-sm font-medium text-slate-900 line-clamp-2">{project.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {project.project_number || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {project.customer || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-slate-500">
                            <MapPin className="h-4 w-4 mr-1" />
                            {project.country || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-slate-500 max-w-[200px]">
                            <User className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="line-clamp-2">{project.project_manager || '-'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-slate-500">
                            <Wind className="h-4 w-4 mr-1" />
                            {project.number_of_turbines ?? 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-slate-500">
                            <ClipboardList className="h-4 w-4 mr-1" />
                            {projectTaskCounts[project.id] || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleActive(project); }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                              project.is_active !== false ? 'bg-green-600' : 'bg-steel-300'
                            }`}
                            title={project.is_active !== false ? 'Project is active (visible everywhere)' : 'Project is inactive (hidden)'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                project.is_active !== false ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <div className="mt-1 text-xs">
                            {project.is_active !== false ? (
                              <span className="text-green-600 font-medium">Active</span>
                            ) : (
                              <span className="text-slate-500">Inactive</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditModal(project); }}
                            className="text-brand-600 hover:text-brand-800 mr-3"
                            title="Edit project"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(project); }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete project"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Task Lists Tab */}
      {activeTab === 'task-lists' && (
        <div className="space-y-6">
          {/* Task Lists Grid */}
          {taskListsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            </div>
          ) : filteredTaskLists.length === 0 ? (
            <div className="text-center py-12 bg-steel-50 rounded-lg">
              <ClipboardList className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No task lists</h3>
              <p className="mt-1 text-sm text-slate-500">
                {taskListSearchTerm
                  ? 'No task lists match your search.'
                  : !showNonDefaultTaskLists
                    ? 'No default task lists found. Check "Show non-default" to see all lists.'
                    : 'Get started by creating a new task list.'}
              </p>
              {!showNonDefaultTaskLists && !taskListSearchTerm && taskLists.some(tl => !tl.is_default) && (
                <button
                  onClick={() => setShowNonDefaultTaskLists(true)}
                  className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
                >
                  <span>Show All Task Lists</span>
                </button>
              )}
              {!taskListSearchTerm && showNonDefaultTaskLists && (
                <button
                  onClick={openCreateTaskListModal}
                  className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Task List</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTaskLists.map(taskList => (
                <div key={taskList.id} className="bg-white border border-brand-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900 truncate">{taskList.name}</h3>
                          {taskList.is_default && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">Default</span>
                          )}
                        </div>
                        {taskList.description && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{taskList.description}</p>
                        )}
                        {taskList.project_name && (
                          <p className="text-xs text-brand-600 mt-1 flex items-center">
                            <Wind className="h-3 w-3 mr-1" />
                            {taskList.project_name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-3 flex items-center space-x-4 text-sm text-slate-500">
                      <span className="flex items-center">
                        <ClipboardList className="h-4 w-4 mr-1" />
                        {taskList.categories?.length || 0} categories
                      </span>
                      <span className="flex items-center">
                        <List className="h-4 w-4 mr-1" />
                        {taskList.categories?.reduce((sum, cat) => sum + (cat.subcategories?.length || 0), 0) || 0} tasks
                      </span>
                    </div>

                    {/* Categories preview */}
                    {taskList.categories?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {taskList.categories.slice(0, 4).map((cat, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: cat.color || '#3B82F6' }}
                          >
                            {cat.name}
                          </span>
                        ))}
                        {taskList.categories.length > 4 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-steel-100 text-slate-600">
                            +{taskList.categories.length - 4} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 pt-3 border-t border-steel-100 flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        Created {new Date(taskList.created_at).toLocaleDateString()}{taskList.created_by_name && ` by ${taskList.created_by_name}`}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleDuplicateTaskList(taskList)}
                          className="p-1.5 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditTaskListModal(taskList)}
                          className="p-1.5 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTaskList(taskList.id, taskList.name)}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Site Status Tab */}
      {activeTab === 'site-status' && (
        <div className="space-y-6">
          {/* Empty page for future development */}
        </div>
      )}


      {/* Task Assignment Modal */}
      {showTaskAssignmentModal && (
        <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingAssignment ? 'Edit Task Assignment' : 'Add Task to Plan'}
              </h3>
              <button
                onClick={() => {
                  setShowTaskAssignmentModal(false);
                  setEditingAssignment(null);
                  setTaskAssignmentData({
                    taskId: '',
                    startTime: '08:00',
                    endTime: '16:00',
                    assignedUsers: []
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Task Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Task:</label>
                <select
                  value={taskAssignmentData.taskId}
                  onChange={(e) => setTaskAssignmentData({...taskAssignmentData, taskId: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                >
                  <option value="">Choose a task...</option>
                  {tasks.filter(task => task.project_id === selectedPlanningProject.id).map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title || task.name} - Turbine {task.turbine_number} ({task.estimated_hours}h)
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={taskAssignmentData.startTime}
                    onChange={(e) => setTaskAssignmentData({...taskAssignmentData, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={taskAssignmentData.endTime}
                    onChange={(e) => setTaskAssignmentData({...taskAssignmentData, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
              
              {/* Duration Display */}
              <div className="text-sm text-slate-600 text-center">
                Duration: {(() => {
                  const start = new Date(`2000-01-01T${taskAssignmentData.startTime}`);
                  const end = new Date(`2000-01-01T${taskAssignmentData.endTime}`);
                  const diff = (end - start) / (1000 * 60 * 60);
                  return diff > 0 ? `${diff.toFixed(1)} hours` : 'Invalid time range';
                })()}
              </div>
              
              {/* User Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign People:</label>
                <div className="border border-steel-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {users.length === 0 ? (
                    <div className="text-slate-500 text-sm italic">No users available</div>
                  ) : (
                    users.map(user => (
                      <label key={user.id} className="flex items-center space-x-2 py-1 hover:bg-steel-50 px-2 rounded">
                        <input
                          type="checkbox"
                          checked={taskAssignmentData.assignedUsers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTaskAssignmentData({
                                ...taskAssignmentData,
                                assignedUsers: [...taskAssignmentData.assignedUsers, user.id]
                              });
                            } else {
                              setTaskAssignmentData({
                                ...taskAssignmentData,
                                assignedUsers: taskAssignmentData.assignedUsers.filter(id => id !== user.id)
                              });
                            }
                          }}
                          className="rounded border-steel-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                      </label>
                    ))
                  )}
                </div>
                {taskAssignmentData.assignedUsers.length > 0 && (
                  <div className="text-sm text-slate-600 mt-2">
                    Selected: {taskAssignmentData.assignedUsers.map(userId => {
                      const user = users.find(u => u.id === userId);
                      return user ? `${user.first_name} ${user.last_name}` : '';
                    }).join(', ')}
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleTaskAssignment}
                  disabled={
                    !taskAssignmentData.taskId ||
                    taskAssignmentData.assignedUsers.length === 0 ||
                    taskAssignmentData.startTime >= taskAssignmentData.endTime
                  }
                  className="flex-1 bg-brand-600 text-white py-2 px-4 rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-steel-400 disabled:cursor-not-allowed"
                >
                  {editingAssignment ? 'Update Assignment' : 'Add to Plan'}
                </button>
                <button
                  onClick={() => {
                    setShowTaskAssignmentModal(false);
                    setEditingAssignment(null);
                    setTaskAssignmentData({
                      taskId: '',
                      startTime: '08:00',
                      endTime: '16:00',
                      assignedUsers: []
                    });
                  }}
                  className="flex-1 bg-steel-300 text-slate-700 py-2 px-4 rounded-md hover:bg-steel-400 focus:outline-none focus:ring-2 focus:ring-steel-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Old time assignment modal removed */}

      {/* Task Creation Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Create Task</h3>
              <button
                onClick={() => setShowTaskModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Name</label>
                <input
                  type="text"
                  value={taskFormData.name}
                  onChange={(e) => setTaskFormData({...taskFormData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({...taskFormData, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Turbine Number</label>
                <select
                  value={taskFormData.turbine_number}
                  onChange={(e) => setTaskFormData({...taskFormData, turbine_number: e.target.value})}
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Select turbine...</option>
                  {getTurbineNumbers().map(num => (
                    <option key={num} value={num}>Turbine {num}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Hours</label>
                <input
                  type="number"
                  value={taskFormData.estimated_hours}
                  onChange={(e) => setTaskFormData({...taskFormData, estimated_hours: e.target.value})}
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                <select
                  value={taskFormData.priority}
                  onChange={(e) => setTaskFormData({...taskFormData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-brand-600 text-white py-2 px-4 rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  Create Task
                </button>
                <button
                  type="button"
                  onClick={() => setShowTaskModal(false)}
                  className="flex-1 bg-steel-300 text-slate-700 py-2 px-4 rounded-md hover:bg-steel-400 focus:outline-none focus:ring-2 focus:ring-steel-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Project Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-slate-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${previewingTaskList ? 'w-[1500px]' : 'w-[1200px]'} h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col transition-all duration-300`}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200 flex-shrink-0">
              <h3 className="text-xl font-semibold text-slate-900">
                {showCreateModal ? 'Create New Project' : 'Edit Project'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedProject(null);
                  resetForm();
                  setPreviewingTaskList(null);
                }}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-steel-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Body - two columns with separate scrolls */}
            <div className="flex-1 flex gap-8 px-6 py-4 min-h-0">
              {/* Left column - Project Details */}
              <div className="flex-1 overflow-y-auto pr-4">
                <div className="space-y-3">
                  <h4 className="text-md font-medium text-slate-900 mb-3">Project Information</h4>

                  {/* 1. Project Name + Color */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className={`w-full p-2 border rounded-md focus:ring-brand-500 focus:border-brand-500 ${formData.name.length >= 50 ? 'border-amber-500' : 'border-steel-300'}`}
                        placeholder="Enter project name"
                        maxLength={50}
                      />
                      {formData.name.length >= 50 && (
                        <p className="text-xs text-amber-600 mt-1">Maximum 50 characters allowed</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={formData.color}
                          onChange={(e) => setFormData({...formData, color: e.target.value})}
                          className="h-[42px] w-16 p-1 border border-steel-300 rounded-md cursor-pointer"
                        />
                        <div
                          className="h-8 w-8 rounded-full border border-steel-300 flex-shrink-0"
                          style={{ backgroundColor: formData.color }}
                        />
                        <span className="text-sm text-slate-600">{formData.color}</span>
                      </div>
                    </div>
                  </div>

                  {/* 2. Project Type + Project Number */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project Type <span className="text-red-500">*</span></label>
                      <select
                        value={formData.project_type}
                        onChange={(e) => setFormData({...formData, project_type: e.target.value})}
                        className={`w-full p-2 border rounded-md focus:ring-brand-500 focus:border-brand-500 bg-white ${
                          !formData.project_type ? 'border-steel-300 text-slate-400' : 'border-steel-300'
                        }`}
                      >
                        <option value="" disabled>Select project type...</option>
                        <option value="service">Service</option>
                        <option value="installation">Installation</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project Number</label>
                      <input
                        type="text"
                        value={formData.project_number}
                        onChange={(e) => setFormData({...formData, project_number: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="e.g., PRJ-2024-001"
                      />
                    </div>
                  </div>

                  {/* 3. Project Manager + Customer */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Project Manager</label>
                      <input
                        type="text"
                        value={formData.project_manager}
                        onChange={(e) => setFormData({...formData, project_manager: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Manager name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
                      <input
                        type="text"
                        value={formData.customer}
                        onChange={(e) => setFormData({...formData, customer: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Customer name"
                      />
                    </div>
                  </div>

                  {/* 4. Country + Number of Turbines */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Country"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Turbines</label>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-slate-900">{projectTurbines.length}</span>
                        <span className="text-sm text-slate-500">turbine{projectTurbines.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>

                  {/* 5. Street, City, Postal Code */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        value={formData.street_address}
                        onChange={(e) => setFormData({...formData, street_address: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Street"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label>
                      <input
                        type="text"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                        placeholder="Postal code"
                      />
                    </div>
                  </div>

                  {/* 5. Administration Coordinates */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Administration Coordinates
                      <span className="ml-2 text-xs font-normal text-slate-500">(creates administration marker on map)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input
                          type="number"
                          step="0.0000001"
                          value={formData.latitude}
                          onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                          className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Latitude (e.g., 51.1234567)"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          step="0.0000001"
                          value={formData.longitude}
                          onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                          className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Longitude (e.g., 17.1234567)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 6. Start Date + End Date */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </div>

                  {/* Check-in/Check-out System */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="need_check"
                      checked={formData.need_check || false}
                      onChange={(e) => setFormData({...formData, need_check: e.target.checked})}
                      className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                    />
                    <label htmlFor="need_check" className="ml-2 block text-sm text-slate-700">
                      Check-in/Check-out System
                    </label>
                  </div>

                  {/* 7. Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows="3"
                      className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                      placeholder="Enter project description"
                    />
                  </div>

                  {/* CPO Type Fields - only visible with CPO subscription */}
                  {hasCPOSubscription && (
                    <div className="pt-4 border-t border-steel-200 mt-4">
                      <h4 className="text-md font-medium text-slate-900 mb-3">Construction Project Order Templates</h4>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type of work for admin</label>
                        <textarea
                          value={formData.type_swo_admin}
                          onChange={(e) => setFormData({...formData, type_swo_admin: e.target.value})}
                          rows="3"
                          className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Default type of work for admin CPO"
                        />
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type of work for TM</label>
                        <textarea
                          value={formData.type_swo_instalation}
                          onChange={(e) => setFormData({...formData, type_swo_instalation: e.target.value})}
                          rows="3"
                          className="w-full p-2 border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Default type of work for TM CPO"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column - Turbine Configuration */}
              <div className="flex-1 overflow-y-auto pl-4 border-l border-steel-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-slate-900">
                    Turbine Configuration ({projectTurbines.length})
                    {savingTurbines && <span className="ml-2 text-sm text-brand-600">(Saving...)</span>}
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newTurbineNumber = projectTurbines.length + 1;
                      const defaultModel = projectTurbines.length > 0 ? projectTurbines[0].model : (formData.model || '');
                      const defaultPowerRating = projectTurbines.length > 0 ? projectTurbines[0].power_rating : (formData.power_rating || '');
                      setProjectTurbines([...projectTurbines, {
                        turbine_number: newTurbineNumber,
                        turbine_id: formData.name ? `${formData.name}-T${String(newTurbineNumber).padStart(2, '0')}` : '',
                        model: defaultModel,
                        power_rating: defaultPowerRating,
                        latitude: '',
                        longitude: '',
                        task_list: null
                      }]);
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <Plus className="w-4 h-4" />
                    Add Turbine
                  </button>
                </div>

                {projectTurbines.length > 0 ? (
                  <div className="space-y-3">
                    {projectTurbines.map((turbine, index) => (
                      <div key={turbine.id || index} className={`p-3 border rounded-md ${index === 0 ? 'border-brand-300 bg-brand-50' : 'border-steel-200 bg-steel-50'}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <h5 className="text-sm font-medium text-slate-700">
                            Turbine {index + 1}
                          </h5>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={turbine.turbine_id || ''}
                              onChange={(e) => handleTurbineChange(index, 'turbine_id', e.target.value)}
                              className="w-full p-1 text-xs border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                              placeholder="Turbine ID"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (projectTurbines.length === 1) {
                                alert('Project must have at least one turbine');
                                return;
                              }
                              const updatedTurbines = projectTurbines.filter((_, i) => i !== index);
                              // Renumber turbines
                              const renumberedTurbines = updatedTurbines.map((t, i) => ({
                                ...t,
                                turbine_number: i + 1
                              }));
                              setProjectTurbines(renumberedTurbines);
                            }}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Remove turbine"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Model and Power Rating per turbine */}
                        <div className="flex gap-2 mb-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Model</label>
                            <input
                              type="text"
                              value={turbine.model || ''}
                              onChange={(e) => handleTurbineChange(index, 'model', e.target.value)}
                              className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                              placeholder="e.g., V150"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Power (MW)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={turbine.power_rating || ''}
                              onChange={(e) => handleTurbineChange(index, 'power_rating', e.target.value)}
                              className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                              placeholder="e.g., 4.2"
                            />
                          </div>
                          {index === 0 && projectTurbines.length > 1 && (
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={applySpecsToAllTurbines}
                                className="px-2 py-2 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 whitespace-nowrap"
                                title="Copy Model and Power to all turbines"
                              >
                                Copy to all
                              </button>
                            </div>
                          )}
                        </div>
                        {/* Coordinates */}
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Latitude</label>
                            <input
                              type="number"
                              step="0.000001"
                              value={turbine.latitude || ''}
                              onChange={(e) => handleTurbineChange(index, 'latitude', e.target.value)}
                              className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                              placeholder="51.123456"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Longitude</label>
                            <input
                              type="number"
                              step="0.000001"
                              value={turbine.longitude || ''}
                              onChange={(e) => handleTurbineChange(index, 'longitude', e.target.value)}
                              className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                              placeholder="4.123456"
                            />
                          </div>
                        </div>
                        {/* Task List Assignment */}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Task List</label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 relative task-list-dropdown">
                              <div
                                onClick={() => setTurbineTaskListDropdownOpen(turbineTaskListDropdownOpen === index ? null : index)}
                                className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500 cursor-pointer bg-white flex items-center justify-between"
                              >
                                <span className={turbine.task_list ? 'text-slate-900' : 'text-slate-500'}>
                                  {turbine.task_list
                                    ? <>
                                        {taskLists.find(tl => tl.id === turbine.task_list)?.name || 'Unknown'}
                                        {taskLists.find(tl => tl.id === turbine.task_list)?.is_default && <span className="ml-1 px-1 py-0.5 text-xs bg-green-100 text-green-700 rounded">Default</span>}
                                        <span className="text-slate-500 ml-1">({taskLists.find(tl => tl.id === turbine.task_list)?.categories?.length || 0} cat.)</span>
                                      </>
                                    : '-- No Task List --'}
                                </span>
                                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${turbineTaskListDropdownOpen === index ? 'rotate-180' : ''}`} />
                              </div>
                              {turbineTaskListDropdownOpen === index && (
                                <div className="absolute z-50 mt-1 w-full bg-white border border-steel-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                                  <div className="p-2 border-b border-steel-200">
                                    <input
                                      type="text"
                                      placeholder="Search task lists..."
                                      value={turbineTaskListSearch[index] || ''}
                                      onChange={(e) => setTurbineTaskListSearch(prev => ({ ...prev, [index]: e.target.value }))}
                                      className="w-full p-2 text-sm border border-steel-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                                      onClick={(e) => e.stopPropagation()}
                                      autoFocus
                                    />
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    <div
                                      onClick={() => {
                                        handleTurbineChange(index, 'task_list', null);
                                        setTurbineTaskListDropdownOpen(null);
                                        setTurbineTaskListSearch(prev => ({ ...prev, [index]: '' }));
                                      }}
                                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-steel-100 ${!turbine.task_list ? 'bg-brand-50 text-brand-700' : 'text-slate-500'}`}
                                    >
                                      -- No Task List --
                                    </div>
                                    {taskLists
                                      .filter(tl => {
                                        const search = (turbineTaskListSearch[index] || '').toLowerCase();
                                        const matchesSearch = !search || tl.name.toLowerCase().includes(search);
                                        // Show default lists, non-default for this project, or currently assigned
                                        const isRelevant = tl.is_default || tl.project === selectedProject?.id || tl.id === turbine.task_list;
                                        return matchesSearch && isRelevant;
                                      })
                                      .sort((a, b) => {
                                        // Sort: default lists first, then by name
                                        if (a.is_default && !b.is_default) return -1;
                                        if (!a.is_default && b.is_default) return 1;
                                        return a.name.localeCompare(b.name);
                                      })
                                      .map(tl => (
                                        <div
                                          key={tl.id}
                                          onClick={() => {
                                            handleTurbineChange(index, 'task_list', tl.id);
                                            setTurbineTaskListDropdownOpen(null);
                                            setTurbineTaskListSearch(prev => ({ ...prev, [index]: '' }));
                                          }}
                                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-steel-100 ${turbine.task_list === tl.id ? 'bg-brand-50 text-brand-700' : 'text-slate-900'}`}
                                        >
                                          {tl.name}
                                          {tl.is_default && <span className="ml-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">Default</span>}
                                          {!tl.is_default && tl.project === selectedProject?.id && <span className="ml-1 px-1.5 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">Project</span>}
                                          <span className="text-slate-500 ml-1">({tl.categories?.length || 0} cat.)</span>
                                        </div>
                                      ))}
                                    {taskLists.filter(tl => {
                                      const search = (turbineTaskListSearch[index] || '').toLowerCase();
                                      const matchesSearch = !search || tl.name.toLowerCase().includes(search);
                                      const isRelevant = tl.is_default || tl.project === selectedProject?.id || tl.id === turbine.task_list;
                                      return matchesSearch && isRelevant;
                                    }).length === 0 && (
                                      <div className="px-3 py-2 text-sm text-slate-500 italic">
                                        No task lists found
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={!selectedProject}
                              onClick={() => {
                                if (!selectedProject) return;
                                setCreatingTaskListForTurbine(index);
                                setEditingTaskListForTurbine(null);
                                setOriginalTaskListName(null);
                                setEditingTaskList(null);
                                setTaskListFormData({
                                  name: '',
                                  description: '',
                                  is_default: false,
                                  project: selectedProject.id,
                                  categories: [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
                                });
                                setExpandedTaskListCategories(new Set());
                                setShowTaskListModal(true);
                              }}
                              className={`px-2 py-2 text-xs font-medium rounded-md focus:outline-none focus:ring-2 whitespace-nowrap ${
                                selectedProject
                                  ? 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 focus:ring-green-500'
                                  : 'text-slate-400 bg-steel-100 border border-steel-200 cursor-not-allowed'
                              }`}
                              title={selectedProject ? "Create new Task List" : "Create project first"}
                            >
                              + Create
                            </button>
                            {turbine.task_list && (
                              <>
                                <button
                                  type="button"
                                  disabled={!selectedProject}
                                  onClick={() => {
                                    if (!selectedProject) return;
                                    const selectedTaskList = taskLists.find(tl => tl.id === turbine.task_list);
                                    if (selectedTaskList) {
                                      // For default task lists, create a copy with project name suffix
                                      // For non-default, use "(edited)" suffix
                                      const newName = selectedTaskList.is_default
                                        ? `${selectedTaskList.name} - ${selectedProject.name}`
                                        : `${selectedTaskList.name} (edited)`;

                                      setEditingTaskListForTurbine(index);
                                      setCreatingTaskListForTurbine(null);
                                      setOriginalTaskListName(selectedTaskList.name);
                                      setEditingTaskList(null); // Not editing existing, will create new
                                      setTaskListFormData({
                                        name: newName,
                                        description: selectedTaskList.description || '',
                                        is_default: false,
                                        project: selectedProject.id,
                                        categories: selectedTaskList.categories?.length > 0
                                          ? selectedTaskList.categories.map(cat => ({
                                              name: cat.name,
                                              color: cat.color || '#3B82F6',
                                              subcategories: cat.subcategories?.length > 0
                                                ? cat.subcategories.map(sub => ({
                                                    name: sub.name,
                                                    description: sub.description || '',
                                                    estimated_hours: sub.estimated_hours || ''
                                                  }))
                                                : [{ name: '', description: '', estimated_hours: '' }]
                                            }))
                                          : [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
                                      });
                                      setExpandedTaskListCategories(new Set());
                                      setShowTaskListModal(true);
                                    }
                                  }}
                                  className={`px-2 py-2 text-xs font-medium rounded-md focus:outline-none focus:ring-2 whitespace-nowrap ${
                                    selectedProject
                                      ? 'text-brand-700 bg-brand-50 border border-brand-200 hover:bg-brand-100 focus:ring-brand-500'
                                      : 'text-slate-400 bg-steel-100 border border-steel-200 cursor-not-allowed'
                                  }`}
                                  title={selectedProject ? "Edit as copy (creates new Task List)" : "Create project first"}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const selectedTaskList = taskLists.find(tl => tl.id === turbine.task_list);
                                    if (selectedTaskList) {
                                      setPreviewingTaskList(selectedTaskList);
                                      setExpandedPreviewCategories(new Set());
                                    }
                                  }}
                                  className="px-2 py-2 text-xs font-medium text-slate-700 bg-steel-100 border border-steel-300 rounded-md hover:bg-steel-200 focus:outline-none focus:ring-2 focus:ring-steel-400 whitespace-nowrap"
                                  title="Preview Task List"
                                >
                                  Show
                                </button>
                              </>
                            )}
                            {index === 0 && projectTurbines.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const taskListToCopy = projectTurbines[0].task_list;
                                  setProjectTurbines(prev => prev.map(t => ({
                                    ...t,
                                    task_list: taskListToCopy
                                  })));
                                }}
                                className="px-2 py-2 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 whitespace-nowrap"
                                title="Copy Task List to all turbines"
                              >
                                Copy to all
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500">
                    <p className="text-sm">Enter the number of turbines to configure them</p>
                  </div>
                )}
              </div>

              {/* Third column - Task List Preview */}
              {previewingTaskList && (
                <div className="w-[300px] overflow-y-auto pl-4 border-l border-steel-200 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-medium text-slate-900">Task List Preview</h4>
                    <button
                      type="button"
                      onClick={() => setPreviewingTaskList(null)}
                      className="text-slate-400 hover:text-slate-600 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="mb-3 p-2 bg-brand-50 rounded-lg border border-brand-200">
                    <p className="text-sm font-medium text-brand-700">{previewingTaskList.name}</p>
                    {previewingTaskList.description && (
                      <p className="text-xs text-brand-600 mt-1">{previewingTaskList.description}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    {previewingTaskList.categories?.length > 0 ? (
                      previewingTaskList.categories.map((category, catIndex) => (
                        <div key={catIndex} className="border border-steel-200 rounded-lg overflow-hidden">
                          <div
                            className="flex items-center p-2 bg-steel-50 cursor-pointer hover:bg-steel-100"
                            onClick={() => {
                              setExpandedPreviewCategories(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(catIndex)) {
                                  newSet.delete(catIndex);
                                } else {
                                  newSet.add(catIndex);
                                }
                                return newSet;
                              });
                            }}
                          >
                            <div
                              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: category.color || '#3B82F6' }}
                            />
                            <span className="text-sm font-medium text-slate-700 flex-1">{category.name}</span>
                            <span className="text-xs text-slate-500 mr-2">
                              {category.subcategories?.length || 0} tasks
                            </span>
                            <svg
                              className={`w-4 h-4 text-slate-400 transition-transform ${expandedPreviewCategories.has(catIndex) ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                          {expandedPreviewCategories.has(catIndex) && category.subcategories?.length > 0 && (
                            <div className="border-t border-steel-100 bg-white">
                              {category.subcategories.map((sub, subIndex) => (
                                <div key={subIndex} className="px-3 py-2 border-b border-steel-50 last:border-b-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-700">{sub.name}</span>
                                    {sub.estimated_hours && (
                                      <span className="text-xs text-slate-500">{sub.estimated_hours}h</span>
                                    )}
                                  </div>
                                  {sub.description && (
                                    <p className="text-xs text-slate-400 mt-0.5">{sub.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No categories defined</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-steel-200 bg-steel-50 flex-shrink-0 rounded-b-xl">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedProject(null);
                  resetForm();
                  setPreviewingTaskList(null);
                }}
                className="px-4 py-2 border border-steel-300 rounded-xl text-sm font-medium text-slate-700 bg-white hover:bg-steel-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={showCreateModal ? handleCreateProject : handleUpdateProject}
                className="px-4 py-2 border border-transparent rounded-xl shadow-lg shadow-brand-500/20 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                {showCreateModal ? 'Create Project' : 'Update Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task List Create/Edit Modal */}
      {showTaskListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-steel-200 flex-shrink-0">
              {editingTaskListForTurbine !== null && (
                <div className="mb-3 px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg text-sm text-brand-700">
                  <strong>Edit as Copy:</strong> You are creating a new Task List based on "{originalTaskListName}". Please change the name before saving.
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Task List Name *</label>
                    <input
                      type="text"
                      value={taskListFormData.name}
                      onChange={(e) => setTaskListFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent w-64 ${
                        editingTaskListForTurbine !== null && taskListFormData.name === originalTaskListName
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-steel-300'
                      }`}
                      placeholder="e.g., Vestas V150 Installation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={taskListFormData.description}
                      onChange={(e) => setTaskListFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="px-3 py-2 border border-steel-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent w-80"
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="flex items-center pt-5">
                    <label className={`flex items-center space-x-2 ${(creatingTaskListForTurbine !== null || editingTaskListForTurbine !== null) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={taskListFormData.is_default}
                        onChange={(e) => setTaskListFormData(prev => ({
                          ...prev,
                          is_default: e.target.checked,
                          project: e.target.checked ? null : prev.project
                        }))}
                        disabled={creatingTaskListForTurbine !== null || editingTaskListForTurbine !== null}
                        className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500 disabled:opacity-50"
                      />
                      <span className="text-sm font-medium text-slate-700">Default</span>
                    </label>
                  </div>
                  {!taskListFormData.is_default && (
                    <div className="pt-1">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Project *</label>
                      <select
                        value={taskListFormData.project || ''}
                        onChange={(e) => setTaskListFormData(prev => ({ ...prev, project: e.target.value ? parseInt(e.target.value) : null }))}
                        disabled={creatingTaskListForTurbine !== null || editingTaskListForTurbine !== null}
                        className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent w-48 disabled:bg-steel-100 disabled:cursor-not-allowed ${
                          !taskListFormData.project ? 'border-red-300 bg-red-50' : 'border-steel-300'
                        }`}
                      >
                        <option value="">Select project...</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>{project.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowTaskListModal(false);
                    setCreatingTaskListForTurbine(null);
                    setEditingTaskListForTurbine(null);
                    setOriginalTaskListName(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content - Two columns */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left column - Existing categories in this TaskList */}
              <div className="w-1/3 border-r border-steel-200 overflow-y-auto p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-slate-900">Categories</h4>
                  <span className="text-sm text-slate-500">
                    {taskListFormData.categories.filter(c => c.name.trim()).length} categories
                  </span>
                </div>

                {taskListFormData.categories.filter(c => c.name.trim()).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 text-steel-300" />
                    <p>No categories yet</p>
                    <p className="text-sm">Create your first category using the form</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {taskListFormData.categories.map((category, catIndex) => (
                      category.name.trim() && (
                        <div key={catIndex} className="border border-steel-200 rounded-lg overflow-hidden">
                          <div
                            className={`flex items-center justify-between p-3 cursor-pointer hover:bg-steel-50 ${
                              expandedTaskListCategories.has(catIndex) ? 'bg-brand-50' : ''
                            }`}
                            onClick={() => toggleTaskListCategoryExpansion(catIndex)}
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div
                                className="w-4 h-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: category.color || '#3B82F6' }}
                              />
                              <span className="font-medium text-sm truncate">{category.name}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0">
                                ({category.subcategories?.filter(s => s.name.trim()).length || 0})
                              </span>
                              {(() => {
                                const totalHours = category.subcategories?.reduce((sum, sub) =>
                                  sum + (parseFloat(sub.estimated_hours) || 0), 0) || 0;
                                return totalHours > 0 ? (
                                  <span className="text-xs text-brand-600 font-medium flex-shrink-0">
                                    {totalHours}h
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTaskListCategory(catIndex);
                                  setTaskListCategoryFormData({
                                    categoryName: category.name,
                                    categoryColor: category.color || '#3B82F6',
                                    subcategories: category.subcategories?.length > 0
                                      ? [...category.subcategories]
                                      : [{ name: '', description: '', estimated_hours: '' }]
                                  });
                                }}
                                className="p-1 text-brand-600 hover:bg-brand-100 rounded"
                                title="Edit category"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeTaskListCategory(catIndex);
                                }}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                                title="Delete category"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {expandedTaskListCategories.has(catIndex) && category.subcategories?.filter(s => s.name.trim()).length > 0 && (
                            <div className="border-t border-steel-100 bg-steel-50 p-2">
                              {category.subcategories.filter(s => s.name.trim()).map((sub, idx) => (
                                <div key={idx} className="text-xs text-slate-600 py-1 pl-6 flex items-center justify-between">
                                  <span className="flex-1 truncate">
                                    • {sub.name}
                                    {sub.description && (
                                      <span className="text-slate-400 ml-1">
                                        - {sub.description.length > 30 ? sub.description.substring(0, 30) + '...' : sub.description}
                                      </span>
                                    )}
                                  </span>
                                  {sub.estimated_hours && (
                                    <span className="text-brand-600 font-medium ml-2">
                                      {sub.estimated_hours}h
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Right column - Add/Edit category form */}
              <div className="flex-1 overflow-y-auto p-6">
                <h4 className="font-medium text-slate-900 mb-4">
                  {editingTaskListCategory !== null ? 'Edit Category' : 'Add New Category'}
                </h4>

                <div className="space-y-6">
                  {/* Category Name and Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={taskListCategoryFormData.categoryName}
                        onChange={(e) => setTaskListCategoryFormData(prev => ({ ...prev, categoryName: e.target.value }))}
                        className="w-full px-3 py-2 border border-steel-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Color
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={taskListCategoryFormData.categoryColor}
                          onChange={(e) => setTaskListCategoryFormData(prev => ({ ...prev, categoryColor: e.target.value }))}
                          className="w-10 h-10 border border-steel-300 rounded-lg cursor-pointer"
                        />
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: taskListCategoryFormData.categoryColor }}
                          />
                          <span>{taskListCategoryFormData.categoryColor}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tasks */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Tasks
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            const newSubcategories = Array(5).fill(null).map(() => ({
                              name: '',
                              description: '',
                              estimated_hours: ''
                            }));
                            setTaskListCategoryFormData(prev => ({
                              ...prev,
                              subcategories: [...prev.subcategories, ...newSubcategories]
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-steel-100 text-slate-700 rounded-lg hover:bg-steel-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add 5 Tasks</span>
                        </button>
                        <button
                          onClick={() => {
                            setTaskListCategoryFormData(prev => ({
                              ...prev,
                              subcategories: [...prev.subcategories, { name: '', description: '', estimated_hours: '' }]
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Task</span>
                        </button>
                      </div>
                    </div>

                    {/* Table View for Tasks */}
                    <div className="border border-steel-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-72 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-steel-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-8">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Task Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Hours</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-steel-200">
                            {taskListCategoryFormData.subcategories.map((subcategory, subIndex) => (
                              <tr key={subIndex} className="hover:bg-steel-50">
                                <td className="px-3 py-2 text-sm text-slate-500">{subIndex + 1}</td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.name}
                                    onChange={(e) => {
                                      const newSubcategories = [...taskListCategoryFormData.subcategories];
                                      newSubcategories[subIndex] = { ...newSubcategories[subIndex], name: e.target.value };
                                      setTaskListCategoryFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Enter name"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={subcategory.estimated_hours || ''}
                                    onChange={(e) => {
                                      const newSubcategories = [...taskListCategoryFormData.subcategories];
                                      newSubcategories[subIndex] = { ...newSubcategories[subIndex], estimated_hours: e.target.value };
                                      setTaskListCategoryFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.description || ''}
                                    onChange={(e) => {
                                      const newSubcategories = [...taskListCategoryFormData.subcategories];
                                      newSubcategories[subIndex] = { ...newSubcategories[subIndex], description: e.target.value };
                                      setTaskListCategoryFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Optional description"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center space-x-1">
                                    <button
                                      onClick={() => {
                                        const newSubcategory = { ...subcategory };
                                        const newSubcategories = [...taskListCategoryFormData.subcategories];
                                        newSubcategories.splice(subIndex + 1, 0, newSubcategory);
                                        setTaskListCategoryFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                                      }}
                                      className="p-1 text-brand-600 hover:bg-brand-100 rounded transition-colors"
                                      title="Duplicate row"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </button>
                                    {taskListCategoryFormData.subcategories.length > 1 && (
                                      <button
                                        onClick={() => {
                                          const newSubcategories = taskListCategoryFormData.subcategories.filter((_, idx) => idx !== subIndex);
                                          setTaskListCategoryFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                                        }}
                                        className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                        title="Delete row"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons for Category */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-steel-200">
                    {editingTaskListCategory !== null && (
                      <button
                        onClick={() => {
                          setEditingTaskListCategory(null);
                          setTaskListCategoryFormData({
                            categoryName: '',
                            categoryColor: '#3B82F6',
                            subcategories: [{ name: '', description: '', estimated_hours: '' }]
                          });
                        }}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors"
                      >
                        Cancel Edit
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (!taskListCategoryFormData.categoryName.trim()) return;

                        const newCategory = {
                          name: taskListCategoryFormData.categoryName.trim(),
                          color: taskListCategoryFormData.categoryColor,
                          subcategories: taskListCategoryFormData.subcategories.filter(s => s.name.trim())
                        };

                        if (editingTaskListCategory !== null) {
                          // Update existing category
                          setTaskListFormData(prev => ({
                            ...prev,
                            categories: prev.categories.map((cat, idx) =>
                              idx === editingTaskListCategory ? newCategory : cat
                            )
                          }));
                          setEditingTaskListCategory(null);
                        } else {
                          // Add new category (replace empty first category or append)
                          setTaskListFormData(prev => {
                            const hasEmptyFirst = prev.categories.length === 1 && !prev.categories[0].name.trim();
                            return {
                              ...prev,
                              categories: hasEmptyFirst ? [newCategory] : [...prev.categories, newCategory]
                            };
                          });
                        }

                        // Reset form
                        setTaskListCategoryFormData({
                          categoryName: '',
                          categoryColor: '#3B82F6',
                          subcategories: [{ name: '', description: '', estimated_hours: '' }]
                        });
                      }}
                      disabled={!taskListCategoryFormData.categoryName.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:bg-steel-400 disabled:cursor-not-allowed"
                    >
                      {editingTaskListCategory !== null ? 'Update Category' : 'Add Category'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-steel-200 flex-shrink-0 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowTaskListModal(false);
                  setCreatingTaskListForTurbine(null);
                  setEditingTaskListForTurbine(null);
                  setOriginalTaskListName(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTaskList}
                disabled={!taskListFormData.name.trim() || taskListFormData.categories.filter(c => c.name.trim()).length === 0 || (!taskListFormData.is_default && !taskListFormData.project)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:bg-steel-400 disabled:cursor-not-allowed"
              >
                {editingTaskListForTurbine !== null ? 'Save as Copy' : (editingTaskList ? 'Update Task List' : 'Create Task List')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Category Management Modal */}
      {showTaskCategoryModal && projectForTasks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-steel-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Task Categories - {projectForTasks.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Manage task categories and subcategories for this project
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTaskCategoryModal(false);
                    setProjectForTasks(null);
                    resetTaskCategoryForm();
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content - Two columns */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left column - Existing categories */}
              <div className="w-1/3 border-r border-steel-200 overflow-y-auto p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-slate-900">Existing Categories</h4>
                  <span className="text-sm text-slate-500">{projectTaskCategories.length} categories</span>
                </div>

                {projectTaskCategories.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 text-steel-300" />
                    <p>No categories yet</p>
                    <p className="text-sm">Create your first category using the form</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projectTaskCategories.map(category => (
                      <div key={category.id} className="border border-steel-200 rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-steel-50"
                          onClick={() => toggleCategoryExpansion(category.id)}
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: category.color || '#3B82F6' }}
                            />
                            <span className="font-medium text-sm truncate">{category.name}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              ({category.subcategories?.length || 0})
                            </span>
                            {(() => {
                              const totalHours = category.subcategories?.reduce((sum, sub) =>
                                sum + (parseFloat(sub.estimated_hours) || 0), 0) || 0;
                              return totalHours > 0 ? (
                                <span className="text-xs text-brand-600 font-medium flex-shrink-0">
                                  {totalHours}h
                                </span>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCategory(category);
                              }}
                              className="p-1 text-brand-600 hover:bg-brand-100 rounded"
                              title="Edit category"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTaskCategory(category.id, category.name);
                              }}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Delete category"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {expandedCategories.has(category.id) && category.subcategories?.length > 0 && (
                          <div className="border-t border-steel-100 bg-steel-50 p-2">
                            {category.subcategories.map((sub, idx) => (
                              <div key={idx} className="text-xs text-slate-600 py-1 pl-6 flex items-center justify-between">
                                <span className="flex-1 truncate">
                                  • {sub.name}
                                  {sub.description && (
                                    <span className="text-slate-400 ml-1">
                                      - {sub.description.length > 30 ? sub.description.substring(0, 30) + '...' : sub.description}
                                    </span>
                                  )}
                                </span>
                                {sub.estimated_hours && (
                                  <span className="text-brand-600 font-medium ml-2">
                                    {sub.estimated_hours}h
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right column - Add/Edit form */}
              <div className="flex-1 overflow-y-auto p-6">
                <h4 className="font-medium text-slate-900 mb-4">
                  {selectedCategoryForEdit ? 'Edit Category' : 'Add New Category'}
                </h4>

                <div className="space-y-6">
                  {/* Category Name and Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={taskCategoryData.categoryName}
                        onChange={(e) => setTaskCategoryData(prev => ({ ...prev, categoryName: e.target.value }))}
                        className="w-full px-3 py-2 border border-steel-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Color
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={taskCategoryData.categoryColor}
                          onChange={(e) => setTaskCategoryData(prev => ({ ...prev, categoryColor: e.target.value }))}
                          className="w-10 h-10 border border-steel-300 rounded-lg cursor-pointer"
                        />
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: taskCategoryData.categoryColor }}
                          />
                          <span>{taskCategoryData.categoryColor}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tasks */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Tasks
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            const newSubcategories = Array(5).fill(null).map(() => ({
                              name: '',
                              description: '',
                              estimated_hours: ''
                            }));
                            setTaskCategoryData(prev => ({
                              ...prev,
                              subcategories: [...prev.subcategories, ...newSubcategories]
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-steel-100 text-slate-700 rounded-lg hover:bg-steel-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add 5 Tasks</span>
                        </button>
                        <button
                          onClick={handleAddSubcategory}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Task</span>
                        </button>
                      </div>
                    </div>

                    {/* Table View for Tasks */}
                    <div className="border border-steel-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-72 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-steel-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-8">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Task Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Hours</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-steel-200">
                            {taskCategoryData.subcategories.map((subcategory, subIndex) => (
                              <tr key={subIndex} className="hover:bg-steel-50">
                                <td className="px-3 py-2 text-sm text-slate-500">{subIndex + 1}</td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.name}
                                    onChange={(e) => handleUpdateSubcategory(subIndex, 'name', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Enter name"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={subcategory.estimated_hours || ''}
                                    onChange={(e) => handleUpdateSubcategory(subIndex, 'estimated_hours', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.description || ''}
                                    onChange={(e) => handleUpdateSubcategory(subIndex, 'description', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Optional description"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center space-x-1">
                                    <button
                                      onClick={() => {
                                        const newSubcategory = { ...subcategory };
                                        const newSubcategories = [...taskCategoryData.subcategories];
                                        newSubcategories.splice(subIndex + 1, 0, newSubcategory);
                                        setTaskCategoryData(prev => ({ ...prev, subcategories: newSubcategories }));
                                      }}
                                      className="p-1 text-brand-600 hover:bg-brand-100 rounded transition-colors"
                                      title="Duplicate row"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                    {taskCategoryData.subcategories.length > 1 && (
                                      <button
                                        onClick={() => handleRemoveSubcategory(subIndex)}
                                        className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                        title="Delete row"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-steel-200">
                    {selectedCategoryForEdit && (
                      <button
                        onClick={resetTaskCategoryForm}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 rounded-lg hover:bg-steel-200 transition-colors"
                      >
                        Cancel Edit
                      </button>
                    )}
                    <button
                      onClick={handleSaveTaskCategory}
                      disabled={!taskCategoryData.categoryName.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:bg-steel-400 disabled:cursor-not-allowed"
                    >
                      {selectedCategoryForEdit ? 'Update Category' : 'Add Category'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProjectManagement;